<template>
    <div>
        <div class="row">
            <div class="col-md-6">
                <div class="form-group">
                    <label class="col-label form-label">{{ $t('signature.copyFrom') }}</label>
                    <a-select style="width: 100%" v-model="model.from_branch" :options="branches" :show-search="true"
                        :filter-option="(input, option) =>
                            option.componentOptions.children[0].text
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            ">
                    </a-select>
                </div>
            </div>
            <div class="col-md-6">
                <div class="form-group">
                    <label class="col-label form-label required">{{ $t('signature.signatureType') }}</label>
                    <a-select style="width: 100%" v-model="model.signature_type" :options="signatureTypes"
                        :show-search="true" :filter-option="(input, option) =>
                            option.componentOptions.children[0].text
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            ">
                    </a-select>
                    <div class="text-danger" v-if="errors.has('signature_type')">
                        {{ errors.first('signature_type') }}
                    </div>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-md-6">
                <div class="form-group">
                    <label class="col-label form-label">{{ $t('signature.positionKh') }}</label>
                    <input class="form-control" v-model="model.position_kh"
                        :class="{ 'is-invalid': errors.has('position_kh') }">
                    <div class="text-danger" v-if="errors.has('position_kh')">
                        {{ errors.first('position_kh') }}
                    </div>
                </div>
            </div>
            <div class="col-md-6">
                <div class="form-group">
                    <label class="col-label form-label required">{{ $t('signature.position') }}</label>
                    <input v-model="model.position" type="text" class="form-control"
                        :class="{ 'is-invalid': errors.has('position') }" />
                    <div class="text-danger" v-if="errors.has('position')">
                        {{ errors.first('position') }}
                    </div>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-md-6">
                <div class="form-group">
                    <label class="col-label form-label">{{ $t('signature.signedKh') }}</label>
                    <input v-model.trim="model.signed_by_kh" type="text" class="form-control"
                        :class="{ 'is-invalid': errors.has('signed_by_kh') }" />
                    <div class="text-danger" v-if="errors.has('signed_by_kh')">
                        {{ errors.first('signed_by_kh') }}
                    </div>
                </div>
            </div>
            <div class="col-md-6">
                <div class="form-group">
                    <label class="col-label form-label required">{{ $t('signature.signBy') }}</label>
                    <input v-model.trim="model.signed_by" type="text" class="form-control"
                        :class="{ 'is-invalid': errors.has('signed_by') }" />
                    <div class="text-danger" v-if="errors.has('signed_by')">
                        {{ errors.first('signed_by') }}
                    </div>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-md-6">
                <div class="form-group">
                    <label class="col-label form-label required">{{ $t('signature.mainProgram') }}</label>
                    <a-select style="width: 100%;" v-model.number="model.acp_id" :options="main_programs"
                        :show-search="true" :filter-option="(input, option) =>
                            option.componentOptions.children[0].text
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            " @change="onMainProgramChange">
                    </a-select>
                    <div class="text-danger" v-if="errors.has('acp_id')">
                        {{ errors.first('acp_id') }}
                    </div>
                </div>
            </div>
            <div class="col-md-6">
                <div class="form-group">
                    <label class="col-label form-label required">{{ $t('signature.subProgram') }}</label>
                    <ts-loading-banner :loading="loadingSubPrograms">
                        <a-select mode="multiple" style="width: 100%" v-model="model.acps_id"
                            :options="computedSubPrograms" :show-search="true" :filter-option="filterOption"
                            :disabled="!model.acp_id" :maxTagTextLength="10" :maxTagCount="1">
                        </a-select>
                    </ts-loading-banner>
                    <div class="text-danger" v-if="errors.has('acps_id')">
                        {{ errors.first('acps_id') }}
                    </div>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-md-12">
                <div class="form-group">
                    <label class="col-md-2 col-form-label required">{{ $t('signature.signatureFile') }}</label>
                    <div class="col-md-3">
                        <ts-button color="primary" @click="$refs.file.click()">{{
                            $t('signature.browseFile')
                            }}</ts-button>
                        <input type="file" ref="file" style="display: none" accept="image/x-png, image/gif, image/jpeg"
                            @change="onFileChange" />
                        <div class="text-danger" v-if="errors.has('file') || errors.has('signatures')">
                            {{ errors.first('file') || errors.first('signatures') }}
                        </div>
                        <img v-if="imagePreview" id="signature" :src="imagePreview" alt="Signature Preview"
                            style="max-width: 200px; max-height: 100px; margin-top: 10px;" />
                    </div>
                </div>
            </div>
        </div>
        <br />
        <div class="row">
            <div class="col-md-12 text-right">
                <slot name="action-button"></slot>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { Errors } from 'form-backend-validation'
import { isEmpty } from 'lodash'

export default {
    props: {
        value: {
            type: Object,
            required: true
        },
        validate: {
            type: Object,
            default: () => new Errors()
        },
        previewImage: {
            type: String,
            default: ''
        },
        subProgramOptions: {
            type: Array,
            default: () => []
        },
        loadingSub: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            errors: new Errors(),
            loading: false,
            fetch_sub_program: false,
            main_programs: [],
            sub_programs: [],
            branches: [],
            signatureTypes: [
                { label: 'Card', value: 'Card' },
                { label: 'Report', value: 'Report' }
            ],
            priview: ''
        }
    },

    created() {
        this.fetchResource()
        this.fetchBranch()
        this.model = this.value
        if (this.previewImage) {
            this.priview = this.previewImage
        }
    },

    computed: {
        ...mapState('signature', ['edit_data']),

        model: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            }
        },

        isUpdate() {
            return !isEmpty(this.edit_data)
        },

        computedSubPrograms() {
            return this.subProgramOptions.length > 0 ? this.subProgramOptions : this.sub_programs
        },

        imagePreview() {
            return this.priview || this.previewImage || ''
        },

        loadingSubPrograms() {
            return this.loadingSub || this.fetch_sub_program
        }
    },

    methods: {
        ...mapActions('signature', ['getMainProgram', 'getSubProgram']),

        filterOption(input, option) {
            return option.componentOptions.children[0].text
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
        },

        async fetchResource() {
            this.loading = true
            await this.getMainProgram()
                .then(response => {
                    this.main_programs = response.data.map(el => ({
                        value: el.acp_id,
                        label: el.acp_name_foreign
                    }))
                })
                .catch(error => {
                    this.$toasted.error(error.message)
                })
                .finally(() => {
                    this.loading = false
                })
        },

        async onMainProgramChange(acp_id) {
            // Clear errors when changing main program
            if (this.errors.has('acps_id')) {
                this.errors.clear('acps_id')
            }

            // Only clear subprograms if this is a new entry (id === 0)
            if (this.model.id === 0) {
                this.model.acps_id = []
            }

            if (!acp_id) {
                this.sub_programs = []
                this.$emit('update-sub-programs', [])
                return
            }

            this.fetch_sub_program = true

            try {
                const response = await this.getSubProgram({
                    filter: {
                        acp_id: acp_id
                    }
                })

                this.sub_programs = response.data.map(el => ({
                    value: el.acps_id,
                    label: el.acps_name_foreign,
                }))

                // Emit to parent to update its sub_programs
                this.$emit('update-sub-programs', this.sub_programs)
            } catch (error) {
                this.$toasted.error(error.message)
                this.sub_programs = []
                this.$emit('update-sub-programs', [])
            } finally {
                this.fetch_sub_program = false
            }
        },

        fetchBranch() {
            this.fetch = true
            this.$store
                .dispatch('signature/getBranch')
                .then(response => {
                    this.branches = response.data.map(el => ({
                        value: el.branch_id,
                        label: el.branch_name_en,
                    }))
                })
                .catch((error) => {
                    this.$toasted.error(error.message)
                })
                .finally(() => {
                    this.fetch = false
                })
        },

        onFileChange(e) {
            let file = e.target.files[0]
            if (file instanceof File || file instanceof Blob) {
                this.model.file = file

                // Clear file/signatures errors when a file is selected
                if (this.errors.has('file')) {
                    this.errors.clear('file')
                }
                if (this.errors.has('signatures')) {
                    this.errors.clear('signatures')
                }
            }

            let reader = new FileReader()
            reader.addEventListener(
                'load',
                function () {
                    this.priview = reader.result
                }.bind(this),
                false
            )
            if (file) {
                if (/\.(jpe?g|png)$/i.test(file.name)) {
                    reader.readAsDataURL(file)
                }
            }
        }
    },

    watch: {
        validate: function (error) {
            this.errors = new Errors(error.errors)
        },

        previewImage: function (newVal) {
            if (newVal) {
                this.priview = newVal
            }
        },

        subProgramOptions: function (newVal) {
            if (newVal && newVal.length > 0) {
                this.sub_programs = newVal
            }
        }
    }
}
</script>